import { Button, Dialog, DialogActions, DialogBody, DialogContent, DialogSurface, DialogTitle, makeStyles, mergeClasses, Option, OptionOnSelectData, Text } from '@fluentui/react-components';
import { DismissRegular } from '@fluentui/react-icons';
import { observer } from 'mobx-react-lite';
import React, { useState } from 'react';
import { useCommonStyles } from '../../../common/hooks';
import { DisqualifyReasonType, disqualifyReasons } from '../../../models';
import { disqualificationRequestSchema } from '../../../models/schemas/disqualification-request.schema';
import { createValidationState } from '../../../shared/validation/zod-validation-state';
import { ValidationMessageList } from '../ValidationMessageList';
import { FormInput } from '../form/FormInput';
import { SelectOptions } from '../form/SelectOptions';

interface DisqualifyScrubbingModalProps {
  onCancelButtonClicked(): void;
  onSaveButtonClicked(disqualifyReason: DisqualifyReasonType, feedback?: string): void;
}

const useStyles = makeStyles({
  modalSurface: {
    maxWidth: '416px',
    width: '100%',
  }
});

const DisqualifyScrubbingModalImpl: React.FC<DisqualifyScrubbingModalProps> = (props) => {
  const {
    onCancelButtonClicked,
    onSaveButtonClicked,
  } = props;

  const commonStyles = useCommonStyles();
  const styles = useStyles();

  const [disqualifyReason, setDisqualifyReason] = useState<DisqualifyReasonType>('Incomplete');
  const [isBeingValidated, setIsBeingValidated] = useState<boolean>(false);
  const [feedback, setFeedback] = useState<string>();

  const disqualificationValidation = createValidationState(disqualificationRequestSchema.safeParse({
    disqualifyReason,
    feedback,
  }));

  const handleDisqualifyReasonSelected = (data: OptionOnSelectData): void => {
    const selectedDisqualifyReason = disqualifyReasons.find((reason) => reason === data.optionValue);
    if (!selectedDisqualifyReason) return;

    setFeedback(undefined);
    setDisqualifyReason(selectedDisqualifyReason);
  };

  const handleSaveButtonClicked = (): void => {
    setIsBeingValidated(true);
    if (!disqualificationValidation.isValid) return;

    onSaveButtonClicked(disqualifyReason, feedback);
  };

  return (
    <Dialog
      open
      onOpenChange={(_, data) => {
        if (!data.open) onCancelButtonClicked();
      }}
    >
      <DialogSurface className={mergeClasses(commonStyles.modalSurface, styles.modalSurface)}>
        <DialogBody className={commonStyles.modalBody}>
          <DialogTitle
            action={(
              <Button
                title='close'
                appearance='transparent'
                icon={<DismissRegular />}
                onClick={() => onCancelButtonClicked()}
              />
            )}
          >
            Disqualify
          </DialogTitle>
          <DialogContent>
            <Text as='p'>
              Are you sure you would like to disqualify? If so, please provide reasoning and feedback.
            </Text>
            <SelectOptions
              label='Disqualify reason'
              onOptionSelect={handleDisqualifyReasonSelected}
              dropdownProps={{
                value: disqualifyReason,
                selectedOptions: [disqualifyReason],
              }}
            >
              {
                disqualifyReasons.map((reason) => (
                  <Option
                    key={reason}
                    value={reason}
                  >
                    {reason}
                  </Option>
                ))
              }
            </SelectOptions>
            {
              disqualifyReason === 'Other' && (
                <FormInput
                  label='Other'
                  required
                  validationMessage={isBeingValidated && disqualificationValidation.fieldErrors.feedback?.length
                    ? <ValidationMessageList messages={disqualificationValidation.fieldErrors.feedback} />
                    : undefined}
                  onChange={(value) => setFeedback(value)}
                  textAreaProps={{
                    value: feedback || '',
                  }}
                />
              )
            }
          </DialogContent>
          <DialogActions>
            <Button
              appearance='secondary'
              onClick={() => onCancelButtonClicked()}
            >
              Cancel
            </Button>
            <Button
              appearance='primary'
              onClick={handleSaveButtonClicked}
            >
              Save
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

export const DisqualifyScrubbingModal = observer(DisqualifyScrubbingModalImpl);
